import { ReactElement, useState } from "react";
import { Button } from "@twilio-paste/core/button";
import { Card } from "@twilio-paste/core/card";
import { Box } from "@twilio-paste/core/box";
import { Heading } from "@twilio-paste/core/heading";
import { Paragraph } from "@twilio-paste/core/paragraph";
import { Separator } from "@twilio-paste/core/separator";
import { CallIcon } from "@twilio-paste/icons/esm/CallIcon";
import { ChatIcon } from "@twilio-paste/icons/esm/ChatIcon";
import { DocumentationIcon } from "@twilio-paste/icons/esm/DocumentationIcon";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalFooterActions,
  ModalHeader,
  ModalHeading,
} from "@twilio-paste/core/modal";

import {
  CardGroupBlock,
  CardType,
  WiseOwlActionType,
  trackingDetails,
  CardValues,
} from "@twiliointernal/e2-commons-shared-definitions";

import { useWiseOwlContext } from "../../../hooks";
import CallbackRequest from "../../callbackRequest/CallbackRequest";

interface CardGroupRendererProps {
  block: CardGroupBlock;
}

const CardGroup = ({ block }: CardGroupRendererProps): ReactElement => {
  const [isOpen, setIsOpen] = useState(false);
  const handleOpen = () => setIsOpen(true);
  const handleClose = () => setIsOpen(false);
  const [showCallbackRequest, setShowCallbackRequest] = useState(false);

  const {
    chatId,
    handlers: {
      handleRedirectAction,
      handleTrackEvent,
      handleUpdateLiveChatPreEngagementValues,
      handleToggleLiveChatPreEngagement,
    },
    applicationProps: { displayName, ingress },
  } = useWiseOwlContext();

  const handleButtonTrackingEvent = (buttonText: string): void => {
    if (handleTrackEvent) {
      handleTrackEvent(
        trackingDetails.wiseOwl.buttonActionClicked(displayName, buttonText),
        {
          chatId,
          ingress,
        }
      );
    }
  };

  const handleButtonAction = (card: CardValues) => {
    const { button, type } = card;

    if (
      type === CardType.PhoneSupport &&
      button.action.type === WiseOwlActionType.CallbackRequest
    ) {
      setShowCallbackRequest(true);
    }

    // TODO: Need to extend CardGroup block API to accommodate this somehow more elegantly
    // from backend. Consider this a workaround
    if (type === CardType.LiveChat) {
      if (
        handleUpdateLiveChatPreEngagementValues &&
        handleToggleLiveChatPreEngagement
      ) {
        if (button.action.type === WiseOwlActionType.LiveChat) {
          const { productCategory, summary } = button.action.values;
          // set pre-engagement values here to prepopulate the form
          handleUpdateLiveChatPreEngagementValues({
            productCategory,
            message: summary,
          });
          handleToggleLiveChatPreEngagement();
          return;
        }
      }
    }

    if (button.action.type === WiseOwlActionType.Redirect) {
      if (handleRedirectAction) {
        handleRedirectAction(button.action.values.redirectUrl);
      }
    }

    if (button.action.type === WiseOwlActionType.Modal) {
      handleOpen();
    }

    handleButtonTrackingEvent(card.type);
  };

  const cardLabelIcon = (cardType: CardType) => {
    const icons = {
      ticket: <DocumentationIcon decorative={false} title="Submit a ticket" />,
      phone: <CallIcon decorative={false} title="Phone support" />,
      chat: <ChatIcon decorative={false} title="Chat with a live agent" />,
    };

    return icons[cardType];
  };

  const cardButtonModal = (card: CardValues) => {
    const {
      type,
      button: { action },
    } = card;
    if (action.type === WiseOwlActionType.Modal) {
      const modalHeadingID = `${type}-modal`;

      return (
        <Modal
          ariaLabelledby={modalHeadingID}
          isOpen={isOpen}
          onDismiss={handleClose}
          size="default"
        >
          <ModalHeader>
            <ModalHeading as="h3" id={modalHeadingID}>
              {action.values.modalTitle}
            </ModalHeading>
          </ModalHeader>
          <Box whiteSpace="pre-wrap" overflow="auto">
            <ModalBody>
              <Paragraph>{action.values.modalBody}</Paragraph>
            </ModalBody>
          </Box>
          <ModalFooter>
            <ModalFooterActions>
              <Button variant="secondary" onClick={handleClose}>
                Close
              </Button>
            </ModalFooterActions>
          </ModalFooter>
        </Modal>
      );
    }
  };

  const cardButtonCallbackRequest = () => {
    if (showCallbackRequest) {
      return (
        <CallbackRequest
          onClose={() => {
            setShowCallbackRequest(false);
          }}
        />
      );
    }
  };

  return (
    <>
      <Box
        display="grid"
        gridTemplateColumns="repeat(3, 1fr)"
        columnGap="space30"
      >
        {block.values.cards.map((card) => (
          <Card key={card.type} padding="space50">
            <Box
              textAlign="left"
              margin="space10"
              display="flex"
              flexDirection="column"
              height="100%"
              justifyContent="space-between"
            >
              <Box>
                <Heading
                  as="h4"
                  variant="heading60"
                  marginBottom="space0"
                  display="flex"
                >
                  <Box paddingRight="space30">{cardLabelIcon(card.type)}</Box>
                  {card.label.text}
                </Heading>
                <Separator orientation="horizontal" verticalSpacing="space40" />
                <Paragraph>{card.description.text}</Paragraph>
              </Box>
              <Box>
                <Button
                  variant="secondary"
                  onClick={() => handleButtonAction(card)}
                >
                  {card.button.text}
                </Button>
              </Box>
              {cardButtonModal(card)}
            </Box>
          </Card>
        ))}
      </Box>
      {cardButtonCallbackRequest()}
    </>
  );
};

export default CardGroup;
